'use client';

import {
  faChevronLeft,
  faChevronRight,
  faCloudArrowDown,
  faCopy,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Button } from '@pickleballinc/react-ui';
import { Credenza, CredenzaContent } from '@pickleballinc/ui/components/ui/credenza';
import clsx from 'clsx';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { MilestoneBar } from '@/modules/players/v3/components/shared/milestone-bar/milestone-bar';
import {
  ACHIEVEMENT_SHARE_PLATFORMS,
  buildAchievementShareText,
} from '@/modules/players/v3/constants/achievements';
import type { ShareableAchievement } from '@/modules/players/v3/types/achievements';
import {
  getAchievementShareUrl,
  getNormalizedAchievementMilestones,
} from '@/modules/players/v3/utils/achievement';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

const DOWNLOAD_LABEL = formatText({ text: 'Download as graphic', role: TextRole.ButtonLabel });
const DOWNLOADING_LABEL = formatText({ text: 'Downloading…', role: TextRole.ButtonLabel });
const DOWNLOAD_ERROR = formatText({
  text: 'Could not download the graphic. Try again.',
  role: TextRole.ErrorMessage,
});

interface ShareAchievementModalProps {
  achievements: ShareableAchievement[];
  open: boolean;
  onClose: () => void;
  isWebview?: boolean;
}

export const ShareAchievementModal = ({
  achievements,
  open,
  onClose,
  isWebview,
}: ShareAchievementModalProps) => {
  const { slug } = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const hasMultiple = achievements.length > 1;
  const achievement = achievements[currentIndex];

  const shareUrl =
    achievement && slug ? getAchievementShareUrl(slug, achievement.achievementId) : '';
  const shareText = achievement ? buildAchievementShareText(achievement.achievementName) : '';

  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setCopied(false);
    }
  }, [open]);

  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  }, [shareUrl]);

  const handleDownload = useCallback(async () => {
    if (!achievement || !slug) return;
    const params = new URLSearchParams({
      slug: String(slug),
      achievementId: String(achievement.achievementId),
    });

    setIsDownloading(true);
    try {
      const response = await fetch(`/api/achievements/graphic?${params.toString()}`);
      if (!response.ok) throw new Error('Graphic request failed');
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${achievement.achievementName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')}.png`;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error(DOWNLOAD_ERROR);
    } finally {
      setIsDownloading(false);
    }
  }, [achievement, slug]);

  if (!achievement) return null;

  return (
    <Credenza
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <CredenzaContent
        className='md:!max-w-[400px] !p-0 !gap-0 !border-0'
        dialogTitle='Share this achievement'
      >
        <div className='flex flex-col gap-4 px-6 pt-4 pb-6'>
          <div className='flex flex-col gap-1'>
            <p className='text-lg font-semibold text-gray-900'>Share this achievement</p>
            <p className='text-sm text-gray-500'>
              {`Share this achievement with your network and let them see what you've accomplished`}
            </p>
          </div>

          <div className='flex items-center gap-2'>
            {hasMultiple ? (
              <button
                type='button'
                aria-label='Previous achievement'
                className='shrink-0 size-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-40'
                onClick={() => setCurrentIndex((i) => i - 1)}
                disabled={currentIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} className='size-4 text-gray-600' />
              </button>
            ) : null}

            <div className='flex-1 flex flex-col items-center bg-gray-50 rounded-2xl pt-4 min-w-0'>
              <div className='size-[120px] shrink-0'>
                <ImageWithFallback
                  src={achievement.logo ?? undefined}
                  fallbackSrc={PICKLEBALL_PLACEHOLDER_IMAGE}
                  alt={achievement.achievementName}
                  className='size-full object-cover rounded-xl'
                />
              </div>

              <div className='flex flex-col items-center pt-4 w-full px-4'>
                <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 border border-gray-300 text-lightblue-900'>
                  {`${achievement.currentValue} / ${achievement.goalValue}`}
                </span>
                <p className='text-lg font-semibold text-gray-900 text-center mt-1'>
                  {achievement.achievementName}
                </p>
                <p className='text-sm text-gray-500 text-center'>
                  {achievement.objectiveDescription}
                </p>
              </div>

              <div className='w-full px-4 py-4'>
                <MilestoneBar milestones={getNormalizedAchievementMilestones(achievement)} />
              </div>
            </div>

            {hasMultiple ? (
              <button
                type='button'
                aria-label='Next achievement'
                className='shrink-0 size-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-40'
                onClick={() => setCurrentIndex((i) => i + 1)}
                disabled={currentIndex === achievements.length - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} className='size-4 text-gray-600' />
              </button>
            ) : null}
          </div>

          {hasMultiple ? (
            <div className='flex items-center justify-center gap-1.5'>
              {achievements.map((_, i) => (
                <button
                  key={i}
                  type='button'
                  aria-label={`Go to achievement ${i + 1}`}
                  className={clsx(
                    'size-2 rounded-full transition-colors',
                    i === currentIndex ? 'bg-brand-600' : 'bg-gray-300'
                  )}
                  onClick={() => setCurrentIndex(i)}
                />
              ))}
            </div>
          ) : null}

          <div className='flex items-center justify-center gap-3 py-2'>
            {ACHIEVEMENT_SHARE_PLATFORMS.map((platform) => (
              <a
                key={platform.name}
                href={shareUrl ? platform.getUrl(shareUrl, shareText) : undefined}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={`Share on ${platform.name}`}
                className='bg-gray-50 hover:bg-gray-100 transition-colors p-2.5 rounded-lg text-gray-900'
              >
                <FontAwesomeIcon icon={platform.icon} className='size-5' />
              </a>
            ))}
          </div>
        </div>

        <div className='flex items-center gap-3 px-6'>
          <div className='flex-1 h-px bg-gray-200' />
          <span className='text-sm text-gray-500'>Or</span>
          <div className='flex-1 h-px bg-gray-200' />
        </div>

        <div className={clsx('flex flex-col gap-3 px-6 pt-6', isWebview ? 'pb-8' : 'pb-6')}>
          <Button
            className='w-full justify-center !bg-brand-700 !text-white gap-2'
            onClick={handleCopyLink}
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className='size-4' />
            {copied ? 'Copied!' : 'Copy link'}
          </Button>
          <Button
            className='w-full justify-center gap-2 !border !border-brand-700 !bg-white !text-brand-700'
            onClick={handleDownload}
            disabled={isDownloading}
          >
            <FontAwesomeIcon icon={faCloudArrowDown} className='size-4' />
            {isDownloading ? DOWNLOADING_LABEL : DOWNLOAD_LABEL}
          </Button>
        </div>
      </CredenzaContent>
    </Credenza>
  );
};
